import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { logError } from "../services/api";
import { Check, X, Ban, Loader2, ShieldCheck, Zap } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Avatar from "../components/Avatar";
import SuperAdminOnlyNote from "../components/SuperAdminOnlyNote";
import api from "../services/api";
import { isSuperAdmin } from "../utils/role";
import RefreshButton from "../components/RefreshButton";
import { formatLagosDateTime } from "../utils/tripLog";

// All withdrawal data and the auto-approve setting come from the backend
// (the "bucket"). Nothing here is hardcoded.

// Keeps the kobo when there are any: 125050 -> ₦1,250.50.
const naira = (kobo) => {
  // A missing amount is unknown, not ₦0.
  if (kobo === null || kobo === undefined || kobo === "" || !Number.isFinite(Number(kobo))) return "—";
  const value = Number(kobo) / 100;
  return "₦" + value.toLocaleString("en-NG", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const PAGE_SIZE = 20;

const STATUS_TABS = [
  { key: "awaiting_approval", label: "Awaiting approval" },
  { key: "processing", label: "Processing" },
  { key: "success", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "", label: "All" },
];

const STATUS_BADGE = {
  awaiting_approval: "bg-amber-100 text-amber-800",
  pending: "bg-gray-100 text-gray-700",
  processing: "bg-blue-100 text-blue-800",
  success: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  reversed: "bg-gray-200 text-gray-700",
};

const STATUS_LABEL = {
  awaiting_approval: "Awaiting approval",
  pending: "Pending",
  processing: "Processing",
  success: "Completed",
  failed: "Failed",
  reversed: "Reversed",
};

const formatDate = (d) =>
  formatLagosDateTime(d, "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const Withdrawals = () => {
  const [autoApprove, setAutoApprove] = useState(null); // null = unknown/loading
  const [togglingAuto, setTogglingAuto] = useState(false);

  const [statusFilter, setStatusFilter] = useState("awaiting_approval");
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  // Decline modal state
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const canEdit = isSuperAdmin();
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/v1/admin/settings/withdrawal-approval");
      setAutoApprove(!!data.autoApprove);
    } catch (error) {
      logError("Failed to load withdrawal setting:", error);
      toast.error(
        error.response?.data?.message || "Failed to load approval setting"
      );
    }
  };

  const loadWithdrawals = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/admin/withdrawals", {
        params: { ...(statusFilter ? { status: statusFilter } : {}), page, limit: PAGE_SIZE },
      });
      const rows = data.data || [];
      // Approving or declining the last row of the last page empties it: step back.
      if (rows.length === 0 && page > 1) {
        setPage(page - 1);
        return;
      }
      setWithdrawals(rows);
      setPageInfo(data.pagination || null);
      setLoadError(null);
      setUpdatedAt(new Date());
    } catch (error) {
      logError("Failed to load withdrawals:", error);
      setLoadError(error.response?.data?.message || "Couldn't load withdrawals.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    loadWithdrawals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  const handleToggleAuto = async () => {
    if (!canEdit || autoApprove === null) return;
    const next = !autoApprove;
    setTogglingAuto(true);
    try {
      const { data } = await api.put("/v1/admin/settings/withdrawal-approval", {
        autoApprove: next,
      });
      setAutoApprove(!!data.autoApprove);
      toast.success(
        data.autoApprove
          ? "Automatic transfers ON — withdrawals pay out instantly."
          : "Manual approval ON — withdrawals now wait for your review."
      );
    } catch (error) {
      logError("Failed to update setting:", error);
      toast.error(
        error.response?.data?.message || "Failed to update approval setting"
      );
    } finally {
      setTogglingAuto(false);
    }
  };

  const handleApprove = async (transfer) => {
    if (!canEdit) return;
    setActioningId(transfer._id);
    try {
      await api.post(`/v1/admin/withdrawals/${transfer._id}/approve`);
      toast.success("Withdrawal approved — now processing.");
      await loadWithdrawals();
    } catch (error) {
      logError("Failed to approve withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve withdrawal"
      );
    } finally {
      setActioningId(null);
    }
  };

  const openDecline = (transfer) => {
    setDeclineTarget(transfer);
    setDeclineReason("");
  };

  const closeDecline = () => {
    if (declining) return;
    setDeclineTarget(null);
    setDeclineReason("");
  };

  const submitDecline = async () => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error("Please enter a reason — the rider will see it.");
      return;
    }
    setDeclining(true);
    try {
      await api.post(`/v1/admin/withdrawals/${declineTarget._id}/decline`, {
        reason,
      });
      toast.success("Withdrawal declined and the rider has been refunded.");
      setDeclineTarget(null);
      setDeclineReason("");
      await loadWithdrawals();
    } catch (error) {
      logError("Failed to decline withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to decline withdrawal"
      );
    } finally {
      setDeclining(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Withdrawals
              </h1>
              <RefreshButton onRefresh={loadWithdrawals} loading={loading} updatedAt={updatedAt} />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Review rider withdrawals and control whether payouts are automatic
              or require your approval.
            </p>
            {!canEdit && <SuperAdminOnlyNote className="mt-3" />}
          </div>

          {/* Auto-approve toggle card */}
          <div className="max-w-3xl bg-white dark:bg-black rounded-xl shadow-sm p-5 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div
                  className={`mt-0.5 h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${
                    autoApprove
                      ? "bg-[#FFF5E6] text-[#FF8C00]"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-800"
                  }`}
                >
                  {autoApprove ? (
                    <Zap className="h-5 w-5" />
                  ) : (
                    <ShieldCheck className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    Automatic transfers
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {autoApprove === null
                      ? "Loading current setting…"
                      : autoApprove
                      ? "ON — rider withdrawals are sent out instantly, no approval needed."
                      : "OFF — every withdrawal waits in the queue below for you to approve or decline."}
                  </p>
                </div>
              </div>

              {/* Switch */}
              {canEdit && (
              <button
                type="button"
                role="switch"
                aria-checked={!!autoApprove}
                aria-label="Toggle automatic transfers"
                disabled={autoApprove === null || togglingAuto}
                onClick={handleToggleAuto}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  autoApprove ? "bg-[#FF8C00]" : "bg-gray-300 dark:bg-gray-600"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    autoApprove ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              )}
            </div>
          </div>

          {/* Status filter tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key || "all"}
                onClick={() => {
                  setStatusFilter(tab.key);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  statusFilter === tab.key
                    ? "bg-[#FF8C00] text-white"
                    : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Withdrawals table */}
          <div className="bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rider
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destination account
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Requested
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="sticky right-0 bg-gray-50 dark:bg-gray-900 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-800">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-10 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]" />
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-10 text-center text-sm text-red-600">
                        {loadError}{" "}
                        <button type="button" onClick={loadWithdrawals} className="underline font-medium">
                          Retry
                        </button>
                      </td>
                    </tr>
                  ) : withdrawals.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-10 text-center text-sm text-gray-500"
                      >
                        No withdrawals in this view.
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((w) => {
                      const account = w.withdrawalAccountId || {};
                      const isAwaiting = w.status === "awaiting_approval";
                      const busy = actioningId === w._id;
                      return (
                        <tr key={w._id} className="group hover:bg-gray-50 dark:hover:bg-gray-900/40">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar
                                name={w.userId?.name || "Rider"}
                                className="h-8 w-8 text-sm"
                              />
                              <div className="ml-3">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {w.userId?.name || "Unknown rider"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {w.userId?.phone || w.userId?.email || ""}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                            {naira(w.amountKobo)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {account.accountNumber ? (
                              <>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {account.accountName || "—"}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {account.accountNumber} · {account.bankName}
                                </div>
                              </>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(w.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                STATUS_BADGE[w.status] || "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {STATUS_LABEL[w.status] || w.status}
                            </span>
                            {w.status === "failed" && w.failureReason ? (
                              <div className="text-xs text-gray-500 mt-1 max-w-[240px] whitespace-normal break-words">
                                {w.failureReason}
                              </div>
                            ) : null}
                          </td>
                          {/* Pinned right so Approve/Decline stay in reach on a phone. */}
                          <td className="sticky right-0 bg-white dark:bg-black group-hover:bg-gray-50 dark:group-hover:bg-gray-900 px-6 py-4 whitespace-nowrap text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]">
                            {isAwaiting && canEdit ? (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleApprove(w)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                                >
                                  {busy ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  onClick={() => openDecline(w)}
                                  disabled={busy}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-900 text-red-600 border border-red-200 dark:border-red-900/50 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors disabled:opacity-50"
                                >
                                  <Ban className="h-3.5 w-3.5" />
                                  Decline
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {pageInfo && !loadError && (
              <nav
                className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 px-6 py-3 text-sm text-gray-600 dark:text-gray-300"
                aria-label="Withdrawal pages"
              >
                <span>
                  Page {pageInfo.page} of {Math.max(pageInfo.totalPages || 1, 1)} ·{" "}
                  {(pageInfo.total ?? 0).toLocaleString()} withdrawals
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPage(pageInfo.page - 1)}
                    disabled={!pageInfo.hasPrev || loading}
                    className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(pageInfo.page + 1)}
                    disabled={!pageInfo.hasNext || loading}
                    className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </nav>
            )}
          </div>
        </main>
      </div>

      {/* Decline modal */}
      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeDecline}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Decline withdrawal
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {naira(declineTarget.amountKobo)} to{" "}
                  {declineTarget.userId?.name || "rider"}. The amount is refunded
                  to their wallet and they will see your reason.
                </p>
              </div>
              <button
                onClick={closeDecline}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason (visible to the rider)
            </label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="e.g. Account name does not match your Zooma profile."
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent outline-none resize-none"
            />

            <div className="flex items-center justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={closeDecline}
                disabled={declining}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDecline}
                disabled={declining || !declineReason.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {declining && <Loader2 className="h-4 w-4 animate-spin" />}
                Decline & refund
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdrawals;
